const normalizeEnvValue = (value: unknown): string | undefined => {
  if (typeof value !== 'string') return undefined;

  const normalized = value.trim();
  return normalized.length > 0 ? normalized : undefined;
};

const clampRate = (value: number) => Math.min(1, Math.max(0, value));

const parseNumberEnv = (value: unknown, fallback: number): number => {
  const normalized = normalizeEnvValue(value);
  if (!normalized) return fallback;

  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? parsed : fallback;
};

const parseRateEnv = (value: unknown, fallback: number): number => {
  return clampRate(parseNumberEnv(value, fallback));
};

const parseBooleanEnv = (value: unknown, fallback: boolean): boolean => {
  const normalized = normalizeEnvValue(value);
  if (!normalized) return fallback;

  return ['1', 'true', 'yes', 'on'].includes(normalized.toLowerCase());
};

const ensureRelativePath = (path: string): string => {
  if (/^https?:\/\//i.test(path)) return path;
  return path.startsWith('/') ? path : `/${path}`;
};

const sentryEnvironment =
  normalizeEnvValue(import.meta.env.VITE_SENTRY_ENVIRONMENT) ?? import.meta.env.MODE;

const isLocalDev = import.meta.env.DEV;
const isSentryProduction = sentryEnvironment === 'production';
const profilingEnabled = parseBooleanEnv(import.meta.env.VITE_SENTRY_ENABLE_PROFILING, !isLocalDev);

export const environment = {
  app: {
    name: normalizeEnvValue(import.meta.env.VITE_APP_NAME) ?? 'ePos Web V2',
    buildId: normalizeEnvValue(import.meta.env.VITE_APP_BUILD_ID),
    cacheStrategy: normalizeEnvValue(import.meta.env.VITE_APP_CACHE_STRATEGY),
    mode: import.meta.env.MODE,
    isDevelopment: import.meta.env.DEV,
    isProduction: import.meta.env.PROD,
  },
  auth: {
    recaptchaSiteKey: normalizeEnvValue(import.meta.env.VITE_RECAPTCHA_SITE_KEY) ?? '',
    azure: {
      clientId: normalizeEnvValue(import.meta.env.VITE_AZURE_CLIENT_ID) ?? '',
      tenantId: normalizeEnvValue(import.meta.env.VITE_AZURE_TENANT_ID) ?? '',
      scopes: ['openid', 'profile', 'User.Read', 'offline_access'],
    },
  },
  googleMaps: {
    apiKey: normalizeEnvValue(import.meta.env.VITE_GOOGLE_MAPS_API_KEY),
  },
  sentry: {
    dsn: normalizeEnvValue(import.meta.env.VITE_SENTRY_DSN),
    environment: sentryEnvironment,
    release: normalizeEnvValue(import.meta.env.VITE_SENTRY_RELEASE),
    dist: normalizeEnvValue(import.meta.env.VITE_SENTRY_DIST),
    tracesSampleRate: parseRateEnv(
      import.meta.env.VITE_SENTRY_TRACES_SAMPLE_RATE,
      isSentryProduction ? 0.2 : 1
    ),
    replaysSessionSampleRate: parseRateEnv(
      import.meta.env.VITE_SENTRY_REPLAYS_SESSION_SAMPLE_RATE,
      isSentryProduction ? 0.1 : 1
    ),
    replaysOnErrorSampleRate: parseRateEnv(
      import.meta.env.VITE_SENTRY_REPLAYS_ON_ERROR_SAMPLE_RATE,
      1
    ),
    profilingEnabled,
    profileSessionSampleRate: profilingEnabled
      ? parseRateEnv(import.meta.env.VITE_SENTRY_PROFILE_SESSION_SAMPLE_RATE, isLocalDev ? 0 : 0.1)
      : 0,
    profileLifecycle: 'trace' as const,
  },
};

export const apiUrl = (path: string): string => ensureRelativePath(path);

export const hubsUrl = (path: string): string => ensureRelativePath(path);
